/**
 * The lock screen (mobile M4) — see `lib/appLock.ts` for when it asks and why.
 *
 * A cold start is locked from its very first render, so no frame of a figure
 * is drawn before the check. The app stays mounted underneath and is made
 * `inert`, never unmounted: a relock five minutes in must not throw away an
 * open sheet or a half-typed search. The cover is opaque, portaled onto
 * <body>, and deliberately not `aria-modal` — the back button's overlay check
 * would otherwise send it an Escape it has no answer for; back on a lock
 * screen just leaves.
 *
 * Off-native this renders its children and nothing else.
 */

import { Fingerprint } from "lucide-react";
import * as React from "react";
import { createPortal } from "react-dom";

import { useAuthStatus } from "@/components/AuthGate";
import { Button } from "@/components/ui/primitives";
import { appLockOn, relockDue, unlock } from "@/lib/appLock";

export function AppLock({ children }: { children: React.ReactNode }) {
  const { data: auth } = useAuthStatus();
  const [locked, setLocked] = React.useState(appLockOn);
  const [failed, setFailed] = React.useState(false);
  const hiddenAt = React.useRef<number | null>(null);
  const asking = React.useRef(false);

  const signedIn = Boolean(auth?.authenticated);
  const signedOut = auth !== undefined && !auth.authenticated;

  // Signed out there is nothing to protect, and a lock carried over from the
  // cold start would ask for a fingerprint the moment a password was typed.
  React.useEffect(() => {
    if (signedOut) setLocked(false);
  }, [signedOut]);

  React.useEffect(() => {
    const changed = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt.current ??= Date.now();
        return;
      }
      if (relockDue(hiddenAt.current)) setLocked(true);
      hiddenAt.current = null;
    };
    document.addEventListener("visibilitychange", changed);
    return () => document.removeEventListener("visibilitychange", changed);
  }, []);

  // Covered while locked and not known to be signed out — including the
  // moment before the auth status arrives, so nothing paints underneath.
  const covering = locked && !signedOut;

  const ask = React.useCallback(async () => {
    if (asking.current) return;
    asking.current = true;
    setFailed(false);
    const ok = await unlock();
    asking.current = false;
    if (ok) setLocked(false);
    else setFailed(true);
  }, []);

  // Ask as soon as there is a signed-in app behind the cover.
  React.useEffect(() => {
    if (covering && signedIn) void ask();
  }, [covering, signedIn, ask]);

  React.useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    root.inert = covering;
    if (covering) root.setAttribute("aria-hidden", "true");
    else root.removeAttribute("aria-hidden");
  }, [covering]);

  return (
    <>
      {children}
      {covering &&
        createPortal(
          <div
            role="dialog"
            aria-labelledby="app-lock-title"
            className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-6 bg-base px-8 pb-[var(--inset-bottom)] pt-[var(--inset-top)] text-center"
          >
            <img src="/icons/icon.svg" alt="" className="h-20 w-20 rounded-[1.4rem] shadow-lift" />
            <div className="space-y-2">
              <h1 id="app-lock-title" className="font-display text-xl font-semibold tracking-[-0.02em] text-ink">
                MyDeck is locked
              </h1>
              <p className="text-sm text-muted">
                {failed
                  ? "That didn't unlock it. Try again with your fingerprint, face or screen lock."
                  : "Unlock with your fingerprint, face or screen lock."}
              </p>
            </div>
            {signedIn && (
              <Button variant="primary" size="lg" onClick={() => void ask()}>
                <Fingerprint className="h-5 w-5" aria-hidden />
                Unlock
              </Button>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
